import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Alert, Heading, Stack, Box, SegmentGroup, HStack, Text, Textarea, Field, Flex, Spacer, Input, Select, createListCollection, Portal, Button, Wrap, DataList, Container, Code, Table} from '@chakra-ui/react';
import axios from 'axios';
import { LuArrowLeft as LuBack, LuDna, LuTestTube, LuTestTubes, LuFileSpreadsheet, LuFolderCheck as LuIngest, LuSquareCheck, LuUser, LuSearch, LuTextSearch as LuSearchResults} from "react-icons/lu"
import PageContent from 'components/ui/PageContent';
import PageTitle, {titleIconSize, titleMB} from 'components/ui/PageTitle';

function ContigPage() {
  const { recordId, start, end } = useParams(); // from URL
  const navigate = useNavigate();
  const [contig, setContig] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContig = async() => {
      try {
        setLoading(true);
        const params = {};
        if (start) params.start = parseInt(start);
        if (end) params.end = parseInt(end);
        console.log(params);
        
        const response = await axios.post(`/api/contig/${recordId}`,{}, { params });
        setContig(response.data);
        console.log("Contig data:", response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching contig', err);
        setError(err.response?.data?.detail || err.message);
      } finally {
        setLoading(false);
      }
    };

    if (recordId) {
      fetchContig();
    }
  }, [recordId, start, end]);

  const stats = [
    { 
      label: "Name", 
      value: contig.name,
      link: `/contig/${recordId}`
    },
    { label: "Project", value: contig.project_name },
    { label: "Organism", value: contig.organism_name },
    { 
      label: "Sample", 
      value: contig.sample_name,
      link: contig.sample_id ? `/samples/${contig.sample_id}` : null
    },
    { label: "Sequence length", value: contig.sequence?.length },
    ...(start ? [{ label: "BLAST hit start", value: start }] : []),
    ...(end ? [{ label: "BLAST hit end", value: end }] : []),
  ];

  return (
    <PageContent>
      {error && (
        <Alert status="error" mb={4}>
          {error}
        </Alert>
      )}
      
      <HStack align={"center"} gap="2em">
        <Box mb={titleMB} cursor="pointer" onClick={() => navigate(-1)}>
          <LuBack size={titleIconSize} mb={titleMB} strokeWidth="2.5" />
        </Box>
        <PageTitle icon={LuDna} title="Contig Details" />
      </HStack>

      {loading ? (
        <Text>Loading contig information...</Text>
      ) : (
        <>
          <DataList.Root orientation={"horizontal"} >
              {stats.map((item) => (
                <DataList.Item key={item.label}>
                  <DataList.ItemLabel>{item.label}</DataList.ItemLabel>
                  <DataList.ItemValue>
                    <Container px="0">
                      {item.link ? (
                        <Link to={item.link} style={{ color: 'inherit', textDecoration: 'underline' }}>
                          {item.value}
                        </Link>
                      ) : (
                        item.value
                      )}
                    </Container>
                  </DataList.ItemValue>
                </DataList.Item>
              ))}
          </DataList.Root>

          {/* CDS Features Table */}
          {contig.cds_features && contig.cds_features.length > 0 && (
            <Box mt={4}>
              <Heading size="md" mb={2}>CDS Features</Heading>
               <Table.ScrollArea borderWidth="1px" rounded="md" height="20rem">
                <Table.Root size="xs" striped stickyHeader>
                    <Table.Header>
                    <Table.Row>
                        <Table.ColumnHeader>Gene ID</Table.ColumnHeader>
                        <Table.ColumnHeader>Name</Table.ColumnHeader>
                        <Table.ColumnHeader>Start</Table.ColumnHeader>
                        <Table.ColumnHeader>End</Table.ColumnHeader>
                    </Table.Row>
                    </Table.Header>
                    <Table.Body>
                    {contig.cds_features.map((cds) => (
                        <Table.Row key={cds.id}>
                        <Table.Cell>{cds.gene_id}</Table.Cell>
                        <Table.Cell>
                            <Link to={`/cds/${cds.id}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                            {cds.name}
                            </Link>
                        </Table.Cell>
                        <Table.Cell>{cds.location_start+1}</Table.Cell>
                        <Table.Cell>{cds.location_end}</Table.Cell>
                        </Table.Row>
                    ))}
                    </Table.Body>
                  </Table.Root>
                </Table.ScrollArea>
            </Box>
          )}

          {/* Nucleotide Sequence */}
          {contig.sequence && (
            <Box mt={4}>
              <Heading size="md" mb={2}>Nucleotide Sequence</Heading>
              <Box 
                borderWidth="1px" 
                rounded="md" 
                height="20rem" 
                overflowY="auto"
              >
                <Code 
                  display="block" 
                  whiteSpace="pre-wrap" 
                  p={4}
                  fontSize="sm"
                  fontFamily="monospace"
                  variant="outline"
                >
                  {contig.sequence}
                </Code>
              </Box>
            </Box>
          )}
        </>
      )}

    </PageContent>
  );
}

export default ContigPage;